import { Link } from "react-router-dom";
import { Check, CalendarPlus, Users, MessageCircle, ArrowRight } from "lucide-react";
import StreamList from "Components/marketing/StreamList";
import { CALCULATOR_STORAGE_KEY } from "commons/earnings.constants";
import { getFromSessionStorage } from "helpers/sessionStorage.helper";
import {
  calculateEarnings,
  formatInrCompact,
  formatCount,
} from "services/earnings.service";

/**
 * The review window, put to work.
 *
 * Two to three days of waiting is the highest-attention dead time in the whole
 * funnel. This replaces a progress bar that was hard-coded to 65% with an
 * honest three-state timeline plus something worth reading.
 */
const TIMELINE = [
  { label: "Submitted", state: "done" },
  { label: "Under review", state: "current" },
  { label: "Decision", state: "todo" },
];

const CHECKLIST = [
  {
    icon: CalendarPlus,
    title: "Decide your first event",
    body: "Pick the session your regulars never miss. That's the one to launch with.",
  },
  {
    icon: Users,
    title: "Gather your member list",
    body: "Names and numbers from your group, ready to invite on day one.",
  },
  {
    icon: MessageCircle,
    title: "Draft the message to your members",
    body: "One short note explaining where bookings are moving, and why.",
  },
];

function WhileWeReview() {
  const stored = getFromSessionStorage(CALCULATOR_STORAGE_KEY, "inputs");
  const hasEstimate =
    Number.isFinite(stored?.members) && Number.isFinite(stored?.bookingRate);

  const estimate = hasEstimate
    ? calculateEarnings({
        members: stored.members,
        bookingRatePct: stored.bookingRate,
      })
    : null;

  return (
    <div className="space-y-10">
      {/* ── Honest status timeline ───────────────────────────────── */}
      <div className="rounded-2xl border border-border/50 bg-card p-6">
        <ol className="flex flex-wrap items-center gap-x-3 gap-y-4">
          {TIMELINE.map((stage, i) => (
            <li key={stage.label} className="flex items-center gap-3">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-bold tabular-nums ${
                  stage.state === "done"
                    ? "border-transparent bg-[hsl(var(--brand-lavender))] text-background"
                    : stage.state === "current"
                      ? "border-[hsl(var(--brand-coral))] text-[hsl(var(--brand-coral))]"
                      : "border-border text-muted-foreground"
                }`}
              >
                {stage.state === "done" ? <Check size={13} /> : i + 1}
              </span>
              <span
                className={`text-sm font-medium ${
                  stage.state === "todo"
                    ? "text-muted-foreground"
                    : "text-foreground"
                }`}
              >
                {stage.label}
              </span>
              {i < TIMELINE.length - 1 && (
                <span className="ml-1 hidden h-px w-10 bg-border sm:block" />
              )}
            </li>
          ))}
        </ol>
        <p className="mt-4 text-sm text-muted-foreground">
          Reviews usually take 2–3 business days. We&apos;ll email you as soon as
          there&apos;s a decision — nothing else is needed from you right now.
        </p>
      </div>

      {/* ── Get a head start ─────────────────────────────────────── */}
      <div>
        <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground">
          Get a head start
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Three things worth sorting out before you&apos;re approved.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {CHECKLIST.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-border/50 bg-card p-5"
            >
              <Icon className="h-5 w-5 text-[hsl(var(--brand-lavender))]" />
              <h3 className="mt-3 font-display text-base font-semibold tracking-tight text-foreground">
                {title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── The reminder of why they're here ─────────────────────── */}
      <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground">
            How your community earns
          </h2>
          <div className="mt-5">
            <StreamList compact />
          </div>
        </div>

        {estimate ? (
          <div className="offset-card self-start bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              The estimate you ran
            </p>
            <p className="mt-3 font-display text-4xl font-bold leading-none tabular-nums text-[hsl(var(--brand-coral))]">
              {formatInrCompact(estimate.annual)}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              a year, with {formatCount(stored.members)} members and{" "}
              {stored.bookingRate}% booking each month.
            </p>
            <Link
              to={`/calculator?members=${stored.members}&rate=${stored.bookingRate}`}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              Adjust the numbers <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="offset-card self-start bg-card p-6">
            <p className="font-display text-2xl font-bold leading-tight tracking-tight text-foreground">
              What could your community earn?
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Two sliders, thirty seconds. Worth knowing before your first event
              goes live.
            </p>
            <Link
              to="/calculator"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              Open the calculator <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default WhileWeReview;
